import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { isCancel } from 'axios';
import { useFormikContext } from 'formik';
import debounce from 'lodash/debounce';

import {
    applySavedParameters,
    configurationOf,
    errorMessage,
    fillUrl,
    findPack,
    matchesStoredConfiguration,
    movementBasisOptionsFor,
    normaliseConfiguration,
    packPayload,
    previewPayload,
    REPORT_KEY,
    savedSetParametersOf,
    SNAPSHOT_COMPLETED,
    SNAPSHOT_FAILED,
    validationSchema,
} from './managementPackUtils';

import { apiClient } from 'Helpers/network';

import { useToast } from 'Hooks/useToast';

const PREVIEW_DEBOUNCE_MS = 400;

const ManagementPackContext = createContext(null);

export const useManagementPackContext = () => {
    const context = useContext(ManagementPackContext);

    if (!context) {
        throw new Error('useManagementPackContext must be used within a ManagementPackProvider');
    }

    return context;
};

const snapshotKey = (pack) => (pack ? `${pack.id}:${pack.snapshotStatus}` : null);

const byPeriodDesc = (a, b) => b.period.localeCompare(a.period) || (b.revision ?? 1) - (a.revision ?? 1) || b.id - a.id;

const initialPacks = (options) => {
    const packs = [...(options.packs || [])];

    if (options.initialPack && !packs.some((pack) => pack.id === options.initialPack.id)) {
        packs.push(options.initialPack);
    }

    return packs.sort(byPeriodDesc);
};

/**
 * State and server calls for the management pack builder. The form values live
 * in the surrounding Formik; this provider derives the pack for the selected
 * period and scope, keeps the preview in step with the form (debounced, with
 * stale requests cancelled), and follows snapshot and export progress over the
 * `.report.generated` broadcasts. Nothing polls.
 */
export const ManagementPackProvider = ({ options, urls, channels, children }) => {
    const { values, setValues, setFieldValue } = useFormikContext();

    // The toast context hands out a new object on every render. Calling it
    // through a ref keeps every callback below, and the channel subscriptions
    // that use them, stable while toasts come and go.
    const toastApi = useToast();
    const toastRef = useRef(toastApi);
    toastRef.current = toastApi;
    const toast = useMemo(
        () => ({
            success: (message) => toastRef.current.success(message),
            info: (message) => toastRef.current.info(message),
            warning: (message) => toastRef.current.warning(message),
            error: (message) => toastRef.current.error(message),
        }),
        [],
    );

    const [packs, setPacks] = useState(() => initialPacks(options));
    const [savedSets, setSavedSets] = useState(options.savedSets || []);
    const [appliedFilterSetId, setAppliedFilterSetId] = useState(options.appliedFilterSetId ?? null);

    const [preview, setPreview] = useState(null);
    // Which pack and snapshot state the shown preview was built from.
    const [previewSource, setPreviewSource] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState(null);

    const [generating, setGenerating] = useState(false);
    const [saving, setSaving] = useState(false);
    const [exportingId, setExportingId] = useState(null);
    const [exportRequesting, setExportRequesting] = useState(false);
    const [issuing, setIssuing] = useState(false);
    const [reissuing, setReissuing] = useState(false);
    // A revision opened by id (?pack=) stays selected until another is chosen.
    const [selectedPackId, setSelectedPackId] = useState(options.initialPack?.id ?? null);

    const schema = useMemo(() => validationSchema(), []);

    const activePack = useMemo(() => findPack(packs, values, options, selectedPackId), [packs, values.settings.period, values.scope, options, selectedPackId]);
    const readOnly = !!activePack?.isIssued;

    const movementBasisOptions = useMemo(
        () => (activePack ? activePack.movementBasisOptions || [] : movementBasisOptionsFor(packs, values, options)),
        [activePack, packs, values.settings.period, values.scope, options],
    );

    const upsertPack = useCallback((pack) => {
        if (!pack) {
            return;
        }

        setPacks((prev) => {
            const existing = prev.find((item) => item.id === pack.id);
            // A pack returned by show/update carries no latestExportId; keep the one we know.
            const merged = existing ? { latestExportId: existing.latestExportId, ...pack } : pack;

            return [...prev.filter((item) => item.id !== pack.id), merged].sort(byPeriodDesc);
        });
    }, []);

    const refreshPack = useCallback(
        (packId) =>
            apiClient
                .get(fillUrl(urls.packShow, { id: packId }))
                .then((response) => upsertPack(response.data?.data))
                .catch(() => {}),
        [urls.packShow, upsertPack],
    );

    // Selecting a period and scope that already has a pack loads that pack's
    // stored configuration; its period and as-at date come from the pack.
    const loadedPackId = useRef(options.initialPack?.id ?? null);
    useEffect(() => {
        if (!activePack || activePack.id === loadedPackId.current) {
            return;
        }

        loadedPackId.current = activePack.id;
        const configuration = normaliseConfiguration(activePack.configuration);

        setValues((prev) => ({
            ...prev,
            ...configuration,
            settings: { ...configuration.settings, period: activePack.period, as_at: activePack.asAt },
        }));
    }, [activePack?.id]);

    // Preview: debounced, and each request cancels the one before it.
    const previewAbort = useRef(null);

    const runPreview = useCallback(
        (nextValues, pack) => {
            if (!schema.isValidSync(nextValues)) {
                previewAbort.current?.abort();
                previewAbort.current = null;
                setPreviewLoading(false);
                setPreviewError($t('Complete the pack settings to see the preview.'));
                return;
            }

            previewAbort.current?.abort();
            const controller = new AbortController();
            previewAbort.current = controller;

            setPreviewLoading(true);
            setPreviewError(null);

            apiClient
                .post(urls.preview, previewPayload(nextValues, pack), { signal: controller.signal })
                .then((response) => {
                    setPreview(response.data?.data ?? null);
                    setPreviewSource(snapshotKey(pack));
                })
                .catch((error) => {
                    if (isCancel(error)) {
                        return;
                    }

                    setPreviewError(errorMessage(error, $t('The preview could not be loaded. Please try again.')));
                })
                .finally(() => {
                    if (previewAbort.current === controller) {
                        previewAbort.current = null;
                        setPreviewLoading(false);
                    }
                });
        },
        [schema, urls.preview],
    );

    const debouncedPreview = useMemo(() => debounce(runPreview, PREVIEW_DEBOUNCE_MS), [runPreview]);

    useEffect(
        () => () => {
            debouncedPreview.cancel();
            previewAbort.current?.abort();
        },
        [debouncedPreview],
    );

    // The capture time re-runs the preview after a refresh that completes
    // without a status change seen in between. The issue time re-runs it once
    // the pack is issued, so the cover picks up who it was issued to.
    const previewKey = JSON.stringify([
        previewPayload(values, activePack),
        activePack?.snapshotStatus ?? null,
        activePack?.generatedAt ?? null,
        activePack?.issuedAt ?? null,
    ]);

    useEffect(() => {
        debouncedPreview(values, activePack);
    }, [previewKey, debouncedPreview]);

    // Snapshot progress on the pack channel. The payload carries the status and
    // progress; a finished snapshot re-reads the pack for its error and dates.
    const packIds = useRef(new Set());
    packIds.current = new Set(packs.map((pack) => pack.id));

    const refreshPackRef = useRef(refreshPack);
    refreshPackRef.current = refreshPack;

    // Subscribed once per channel: a re-subscribe leaves the channel first, and
    // a broadcast landing in that gap would be lost.
    useEffect(() => {
        if (!channels?.pack || !window.Echo) {
            return undefined;
        }

        window.Echo.private(channels.pack).listen('.report.generated', (event) => {
            const report = event?.report;

            if (!report?.id || !packIds.current.has(report.id)) {
                return;
            }

            setPacks((prev) =>
                prev.map((pack) =>
                    pack.id === report.id
                        ? {
                              ...pack,
                              status: report.status ?? pack.status,
                              snapshotStatus: report.snapshot_status ?? pack.snapshotStatus,
                              snapshotProgress: report.snapshot_progress ?? pack.snapshotProgress,
                              slideCount: report.slide_count ?? pack.slideCount,
                          }
                        : pack,
                ),
            );

            if (report.snapshot_status === SNAPSHOT_COMPLETED || report.snapshot_status === SNAPSHOT_FAILED) {
                refreshPackRef.current(report.id);
            }
        });

        return () => window.Echo.leave(channels.pack);
    }, [channels?.pack]);

    // Export completion on the ReportGeneration channel.
    const exportingRef = useRef(null);
    exportingRef.current = exportingId;

    const exportPackIdRef = useRef(null);

    const checkExport = useCallback(
        (generationId) =>
            apiClient
                .get(fillUrl(urls.exportStatus, { id: generationId }))
                .then((response) => {
                    const statusKey = response.data?.item?.statusKey;

                    if (exportingRef.current !== generationId) {
                        return;
                    }

                    if (statusKey === 'completed') {
                        setExportingId(null);
                        const packId = exportPackIdRef.current;
                        setPacks((prev) => prev.map((pack) => (pack.id === packId ? { ...pack, latestExportId: generationId } : pack)));
                        toast.success($t('Your management pack is ready and the download has started.'));
                        window.location.href = fillUrl(urls.exportDownload, { id: generationId });
                    } else if (statusKey === 'failed') {
                        setExportingId(null);
                        toast.error($t('The management pack could not be exported. Please try again.'));
                    }
                })
                .catch(() => {}),
        [urls.exportStatus, urls.exportDownload, toast],
    );

    const checkExportRef = useRef(checkExport);
    checkExportRef.current = checkExport;

    useEffect(() => {
        if (!channels?.generation || !window.Echo) {
            return undefined;
        }

        window.Echo.private(channels.generation).listen('.report.generated', (event) => {
            const id = event?.report?.id;

            if (id && id === exportingRef.current) {
                checkExportRef.current(id);
            }
        });

        return () => window.Echo.leave(channels.generation);
    }, [channels?.generation]);

    const generate = useCallback(() => {
        if (!schema.isValidSync(values)) {
            toast.error($t('Complete the pack settings before generating the pack.'));
            return Promise.resolve();
        }

        setGenerating(true);

        return apiClient
            .post(urls.packsStore, { ...packPayload(values), report_filter_set_id: appliedFilterSetId })
            .then((response) => {
                const pack = response.data?.data;
                loadedPackId.current = pack?.id ?? loadedPackId.current;
                upsertPack(pack);
                setSelectedPackId(pack?.id ?? null);
                toast.info($t('Capturing the pack data. The preview updates when it is ready.'));
            })
            .catch((error) => toast.error(errorMessage(error, $t('The pack could not be generated. Please try again.'))))
            .finally(() => setGenerating(false));
    }, [schema, values, urls.packsStore, appliedFilterSetId, upsertPack, toast]);

    // Saves the given configuration to the pack and resolves with the saved pack.
    const saveFormToPack = useCallback(
        (configuration) =>
            apiClient.put(activePack.urls.update, { configuration, lock_version: activePack.lockVersion }).then((response) => {
                upsertPack(response.data?.data);
                const warning = response.data?.meta?.warning;

                if (warning) {
                    toast.warning(warning.message);
                }

                return response.data;
            }),
        [activePack, upsertPack, toast],
    );

    const saveToPack = useCallback(() => {
        if (!activePack || activePack.isIssued) {
            return Promise.resolve();
        }

        setSaving(true);

        return saveFormToPack(packPayload(values).configuration)
            .then((body) => {
                if (!body?.meta?.warning) {
                    toast.success($t('The configuration was saved to this pack.'));
                }
            })
            .catch((error) => toast.error(errorMessage(error, $t('The configuration could not be saved. Please try again.'))))
            .finally(() => setSaving(false));
    }, [activePack, values, saveFormToPack, toast]);

    const commentarySaves = useRef({});

    const saveCommentary = useCallback(
        (pageKey, body) => {
            if (!activePack || activePack.isIssued || activePack.snapshotStatus !== SNAPSHOT_COMPLETED) {
                return Promise.resolve(false);
            }

            // Resolves true once saved, so the field knows its text is no longer unsaved.
            const request = apiClient
                .put(fillUrl(urls.packCommentary, { id: activePack.id, pageKey }), { body })
                .then((response) => {
                    const saved = response.data?.data?.body ?? body;
                    // Typing over a draft in the box settles it: the box no longer shows a draft.
                    const settled = (commentary) =>
                        commentary.state === 'draft'
                            ? { ...commentary, text: saved, state: 'text', draft: null, carriedForward: null }
                            : { ...commentary, text: saved };

                    setPreview((prev) =>
                        prev
                            ? {
                                  ...prev,
                                  pages: prev.pages.map((page) =>
                                      page.commentary?.pageKey === pageKey ? { ...page, commentary: settled(page.commentary) } : page,
                                  ),
                                  commentaries: { ...(prev.commentaries || {}), [pageKey]: saved },
                              }
                            : prev,
                    );

                    return true;
                })
                .catch((error) => {
                    toast.error(errorMessage(error, $t('The commentary could not be saved. Please try again.')));

                    return false;
                })
                .finally(() => {
                    if (commentarySaves.current[pageKey] === request) {
                        delete commentarySaves.current[pageKey];
                    }
                });

            commentarySaves.current[pageKey] = request;

            return request;
        },
        [activePack, urls.packCommentary, toast],
    );

    // A draft action waits for the page's commentary save in flight (leaving
    // the field flushes it), so the two never race.
    const afterCommentarySave = useCallback((pageKey) => Promise.resolve(commentarySaves.current[pageKey]), []);

    // Re-run the preview now, rather than after the debounce, so a status set
    // or removed by hand shows straight away.
    const refreshPreview = useCallback(() => {
        debouncedPreview.cancel();
        runPreview(values, activePack);
    }, [debouncedPreview, runPreview, values, activePack]);

    // Resolves once saved; rejects with the request error, so the modal can
    // show a validation message.
    const saveStatusOverride = useCallback(
        (subjectType, subjectKey, { status, note }) => {
            if (!activePack || activePack.isIssued) {
                return Promise.reject(new Error('read-only'));
            }

            return apiClient.put(fillUrl(urls.packStatusOverride, { id: activePack.id, subjectType, subjectKey }), { status, note }).then(() => {
                toast.success($t('The status was set manually.'));
                refreshPreview();
            });
        },
        [activePack, urls.packStatusOverride, refreshPreview, toast],
    );

    const removeStatusOverride = useCallback(
        (subjectType, subjectKey) => {
            if (!activePack || activePack.isIssued) {
                return Promise.resolve(false);
            }

            return apiClient
                .delete(fillUrl(urls.packStatusOverride, { id: activePack.id, subjectType, subjectKey }))
                .then(() => {
                    toast.success($t('The manual status was removed.'));
                    refreshPreview();

                    return true;
                })
                .catch((error) => {
                    toast.error(errorMessage(error, $t('The manual status could not be removed. Please try again.')));

                    return false;
                });
        },
        [activePack, urls.packStatusOverride, refreshPreview, toast],
    );

    // Saving the setting to the pack writes or removes its drafts. Without a
    // pack it is only a form value, stored when the pack is generated.
    const saveDraftSetting = useCallback(
        (next) => {
            if (!activePack || activePack.isIssued) {
                return Promise.resolve();
            }

            // Only the switch is saved: the pack's stored configuration goes up with
            // draft_commentary changed, so other unsaved form edits stay unsaved.
            const stored = normaliseConfiguration(activePack.configuration);
            const configuration = configurationOf({
                ...stored,
                settings: { ...stored.settings, period: activePack.period, as_at: activePack.asAt, draft_commentary: next },
            });

            return saveFormToPack(configuration)
                .then((body) => {
                    const pack = body?.data;

                    if (!next) {
                        if (body?.meta?.draftsCleared > 0) {
                            toast.success($t('Untouched draft commentary was removed.'));
                        }
                    } else if (pack?.snapshotStatus === SNAPSHOT_COMPLETED) {
                        toast.success($t('Draft commentary was written from the captured figures.'));
                    }

                    setFieldValue('settings.draft_commentary', next);
                    debouncedPreview.cancel();
                    runPreview({ ...values, settings: { ...values.settings, draft_commentary: next } }, pack ?? activePack);
                })
                .catch((error) => {
                    // The pack still has the setting it had, so the toggle shows it again.
                    setFieldValue('settings.draft_commentary', !next);
                    toast.error(errorMessage(error, $t('The configuration could not be saved. Please try again.')));
                });
        },
        [activePack, values, saveFormToPack, debouncedPreview, runPreview, setFieldValue, toast],
    );

    const regenerateDraft = useCallback(
        (pageKey) => {
            if (!activePack || activePack.isIssued) {
                return Promise.resolve(false);
            }

            // Saving bumps lock_version, which cancels a queued export, so only unsaved changes are saved.
            return afterCommentarySave(pageKey)
                .then(() => (matchesStoredConfiguration(values, activePack) ? null : saveFormToPack(packPayload(values).configuration)))
                .then(() => apiClient.post(fillUrl(urls.packCommentaryDraft, { id: activePack.id, pageKey })))
                .then(() => {
                    refreshPreview();

                    return true;
                })
                .catch((error) => {
                    toast.error(errorMessage(error, $t('The draft could not be written. Please try again.')));

                    return false;
                });
        },
        [activePack, values, afterCommentarySave, saveFormToPack, urls.packCommentaryDraft, refreshPreview, toast],
    );

    // `use` is 'draft' (Accept, Use new draft) or 'carried_forward' (Use this instead).
    const chooseDraft = useCallback(
        (pageKey, use) => {
            if (!activePack || activePack.isIssued) {
                return Promise.resolve(false);
            }

            return afterCommentarySave(pageKey)
                .then(() => apiClient.put(fillUrl(urls.packCommentaryDraft, { id: activePack.id, pageKey }), { use }))
                .then(() => {
                    refreshPreview();

                    return true;
                })
                .catch((error) => {
                    toast.error(errorMessage(error, $t('The commentary could not be saved. Please try again.')));

                    return false;
                });
        },
        [activePack, afterCommentarySave, urls.packCommentaryDraft, refreshPreview, toast],
    );

    const dismissDraft = useCallback(
        (pageKey) => {
            if (!activePack || activePack.isIssued) {
                return Promise.resolve(false);
            }

            return afterCommentarySave(pageKey)
                .then(() => apiClient.delete(fillUrl(urls.packCommentaryDraft, { id: activePack.id, pageKey })))
                .then(() => {
                    refreshPreview();

                    return true;
                })
                .catch((error) => {
                    toast.error(errorMessage(error, $t('The draft could not be dismissed. Please try again.')));

                    return false;
                });
        },
        [activePack, afterCommentarySave, urls.packCommentaryDraft, refreshPreview, toast],
    );

    const exportPack = useCallback(() => {
        if (!activePack) {
            return Promise.resolve();
        }

        const packId = activePack.id;
        setExportRequesting(true);

        // The export renders the pack's stored configuration, so a draft
        // saves the form first.
        const saved = activePack.isIssued
            ? Promise.resolve()
            : apiClient
                  .put(activePack.urls.update, { configuration: packPayload(values).configuration, lock_version: activePack.lockVersion })
                  .then((response) => {
                      upsertPack(response.data?.data);
                      const warning = response.data?.meta?.warning;

                      if (warning) {
                          toast.warning(warning.message);
                      }
                  });

        return saved
            .then(() => apiClient.post(activePack.urls.exports))
            .then((response) => {
                const generationId = response.data?.data?.id;

                exportPackIdRef.current = packId;
                exportingRef.current = generationId;
                setExportingId(generationId);
                toast.info($t('Building the PowerPoint file. The download starts when it is ready.'));

                // The job may already have finished before the listener could hear it.
                checkExport(generationId);
            })
            .catch((error) => {
                if (error?.response?.status === 422) {
                    toast.error(errorMessage(error, $t('This pack has too many slides to export.')));
                } else if (error?.response?.status === 409) {
                    toast.error(errorMessage(error, $t('The pack data is not ready yet. Try again when it has finished.')));
                } else {
                    toast.error($t('The management pack could not be exported. Please try again.'));
                }
            })
            .finally(() => setExportRequesting(false));
    }, [activePack, values, upsertPack, checkExport, toast]);

    // Saves the form to the draft, then issues it. Resolves true once issued.
    const issuePack = useCallback(
        (issuedTo) => {
            if (!activePack || activePack.isIssued) {
                return Promise.resolve(false);
            }

            setIssuing(true);

            return (matchesStoredConfiguration(values, activePack) ? Promise.resolve() : saveFormToPack(packPayload(values).configuration))
                .then(() => apiClient.post(activePack.urls.issue, { issued_to: issuedTo?.trim() || null }))
                .then((response) => {
                    upsertPack(response.data?.data);
                    // The revision it replaces is now superseded.
                    packs
                        .filter(
                            (pack) =>
                                pack.isIssued && !pack.isSuperseded && pack.period === activePack.period && pack.scopeSignature === activePack.scopeSignature,
                        )
                        .forEach((pack) => refreshPack(pack.id));
                    // Later drafts in the same scope can now take it as their movement basis.
                    packs
                        .filter((pack) => !pack.isIssued && pack.period > activePack.period && pack.scopeSignature === activePack.scopeSignature)
                        .forEach((pack) => refreshPack(pack.id));
                    toast.success($t('The pack was issued. Its figures and commentary are now locked.'));

                    return true;
                })
                .catch((error) => {
                    toast.error(errorMessage(error, $t('The pack could not be issued. Please try again.')));

                    return false;
                })
                .finally(() => setIssuing(false));
        },
        [activePack, packs, values, saveFormToPack, upsertPack, refreshPack, toast],
    );

    // Opens a draft revision of the active issued pack and selects it.
    const reissuePack = useCallback(() => {
        if (!activePack?.isIssued) {
            return Promise.resolve(false);
        }

        setReissuing(true);

        return apiClient
            .post(activePack.urls.reissue)
            .then((response) => {
                const pack = response.data?.data;
                upsertPack(pack);
                setSelectedPackId(pack?.id ?? null);
                toast.info($t('Revision {{revision}} is open as a draft. Capturing the pack data.', { revision: pack?.revision }));

                return true;
            })
            .catch((error) => {
                toast.error(errorMessage(error, $t('The pack could not be reissued. Please try again.')));

                return false;
            })
            .finally(() => setReissuing(false));
    }, [activePack, upsertPack, toast]);

    const loadSavedSet = useCallback(
        (set) => {
            setValues((prev) => applySavedParameters(prev, set.parameters || {}));
            setAppliedFilterSetId(set.id);
            toast.success($t('Configuration loaded: {{name}}', { name: set.name }));
        },
        [setValues, toast],
    );

    const saveCurrentAsSet = useCallback(
        ({ name, isShared, isDefault = false }) =>
            apiClient
                .post(urls.filterSetsStore, {
                    report_key: REPORT_KEY,
                    name,
                    parameters: savedSetParametersOf(values),
                    is_shared: isShared,
                    is_default: isDefault,
                })
                .then((response) => {
                    const set = response.data?.data;
                    setSavedSets((prev) => [...prev.map((item) => (set.is_default ? { ...item, is_default: false } : item)), set]);
                    setAppliedFilterSetId(set.id);
                    toast.success($t('The configuration was saved.'));
                    return set;
                })
                .catch((error) => {
                    toast.error(errorMessage(error, $t('The configuration could not be saved. Please try again.')));
                    throw error;
                }),
        [urls.filterSetsStore, values, toast],
    );

    const setDefaultSet = useCallback(
        (set) =>
            apiClient
                .put(fillUrl(urls.filterSetUpdate, { id: set.id }), {
                    name: set.name,
                    description: set.description,
                    parameters: set.parameters || {},
                    is_default: true,
                })
                .then((response) => {
                    const updated = response.data?.data;
                    setSavedSets((prev) => prev.map((item) => (item.id === updated.id ? updated : { ...item, is_default: false })));
                    toast.success($t('{{name}} is now the default for this event.', { name: set.name }));
                })
                .catch((error) => toast.error(errorMessage(error, $t('The default could not be changed. Please try again.')))),
        [urls.filterSetUpdate, toast],
    );

    const value = {
        options,
        urls,
        packs,
        activePack,
        readOnly,
        movementBasisOptions,
        preview,
        // True once the shown preview reflects the active pack's completed
        // snapshot, so its commentary is the pack's real text.
        previewHasPackCommentary: !!activePack && activePack.snapshotStatus === SNAPSHOT_COMPLETED && previewSource === snapshotKey(activePack),
        previewLoading,
        previewError,
        generating,
        generate,
        saving,
        saveToPack,
        saveCommentary,
        saveDraftSetting,
        regenerateDraft,
        chooseDraft,
        dismissDraft,
        refreshPreview,
        saveStatusOverride,
        removeStatusOverride,
        exporting: exportRequesting || exportingId !== null,
        exportPack,
        issuing,
        issuePack,
        reissuing,
        reissuePack,
        selectedPackId,
        setSelectedPackId,
        savedSets,
        appliedFilterSetId,
        loadSavedSet,
        saveCurrentAsSet,
        setDefaultSet,
    };

    return <ManagementPackContext.Provider value={value}>{children}</ManagementPackContext.Provider>;
};

export default ManagementPackContext;
